import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Check, Sparkles } from "lucide-react";
import { useLeadCapture } from "@/context/LeadCaptureContext";

const PLANS = [
  {
    id: "start",
    name: "Start",
    desc: "För enskilda dagis och små verksamheter.",
    price: { month: 490, year: 390 },
    features: [
      "Upp till 30 hundar",
      "Bokningssystem",
      "Digitala hundprofiler",
      "Fakturering (50/mån)",
      "E-postsupport",
    ],
    cta: "Starta gratis",
    testid: "pricing-start",
  },
  {
    id: "pro",
    name: "Pro",
    desc: "Allt-i-ett för växande pensionat och dagis.",
    price: { month: 990, year: 790 },
    features: [
      "Upp till 200 hundar",
      "Alla moduler (Hotell, Dagis, Walking)",
      "Automatisk månadsfakturering",
      "Ägar-app med bilduppdateringar",
      "GPS-rutter för promenader",
      "Prioriterad support",
    ],
    cta: "Starta gratis",
    popular: true,
    testid: "pricing-pro",
  },
  {
    id: "enterprise",
    name: "Enterprise",
    desc: "För kedjor och stora anläggningar.",
    price: { month: null, year: null },
    features: [
      "Obegränsat antal hundar",
      "Flera anläggningar / orter",
      "Egen onboarding-konsult",
      "API & integrationer",
      "SSO & avancerad behörighet",
      "Dedikerad support 24/7",
    ],
    cta: "Kontakta säljteam",
    testid: "pricing-enterprise",
  },
];

export default function Pricing() {
  const [annual, setAnnual] = useState(false);
  const { openLeadCapture } = useLeadCapture();

  const handleClick = (plan) => {
    openLeadCapture(`Plan: ${plan.name}`);
  };

  return (
    <section id="pricing" data-testid="pricing-section" className="relative py-24 md:py-32 bg-slate-50/60 dark:bg-slate-900/30 border-y border-slate-200 dark:border-slate-800">
      <div className="mx-auto max-w-7xl px-6 lg:px-12">
        <div className="text-center max-w-2xl mx-auto">
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-emerald-600 dark:text-emerald-400">
            Priser
          </div>
          <h2 className="font-display mt-4 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900 dark:text-white">
            Enkel prissättning. Inga överraskningar.
          </h2>
          <p className="mt-5 text-lg text-slate-600 dark:text-slate-300">
            Prova gratis i 14 dagar. Ingen bindningstid, ingen kortuppgift.
          </p>

          <div className="mt-8 inline-flex items-center rounded-full bg-white dark:bg-slate-800 p-1 border border-slate-200 dark:border-slate-700 text-sm font-medium">
            <button
              data-testid="pricing-toggle-month"
              onClick={() => setAnnual(false)}
              className={`px-4 py-2 rounded-full transition-colors ${
                !annual ? "bg-slate-900 dark:bg-emerald-500 text-white" : "text-slate-600 dark:text-slate-300"
              }`}
            >
              Månadsvis
            </button>
            <button
              data-testid="pricing-toggle-year"
              onClick={() => setAnnual(true)}
              className={`px-4 py-2 rounded-full transition-colors flex items-center gap-2 ${
                annual ? "bg-slate-900 dark:bg-emerald-500 text-white" : "text-slate-600 dark:text-slate-300"
              }`}
            >
              Årligen
              <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${annual ? "bg-emerald-300 text-emerald-900" : "bg-emerald-100 text-emerald-700"}`}>
                -20%
              </span>
            </button>
          </div>
        </div>

        <div className="mt-14 grid md:grid-cols-3 gap-6 items-stretch">
          {PLANS.map((plan) => {
            const price = annual ? plan.price.year : plan.price.month;
            const popular = plan.popular;
            return (
              <div
                key={plan.id}
                data-testid={plan.testid}
                className={`relative flex flex-col rounded-3xl p-8 transition-all ${
                  popular
                    ? "bg-slate-900 text-white border-2 border-emerald-500 shadow-2xl lg:scale-[1.03] lg:-translate-y-1"
                    : "bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 hover:-translate-y-1 hover:shadow-xl"
                }`}
              >
                {popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 rounded-full bg-emerald-500 text-white px-3 py-1 text-xs font-bold uppercase tracking-wider shadow-lg">
                    <Sparkles className="h-3 w-3" />
                    Populärast
                  </div>
                )}
                <div>
                  <h3 className={`font-display text-2xl font-bold ${popular ? "text-white" : "text-slate-900 dark:text-white"}`}>
                    {plan.name}
                  </h3>
                  <p className={`mt-2 text-sm ${popular ? "text-slate-300" : "text-slate-500 dark:text-slate-400"}`}>
                    {plan.desc}
                  </p>
                </div>
                <div className="mt-6 mb-6">
                  {price !== null ? (
                    <div className="flex items-baseline gap-2">
                      <span className={`font-display text-5xl font-bold ${popular ? "text-white" : "text-slate-900 dark:text-white"}`}>
                        {price}
                      </span>
                      <span className={`text-sm ${popular ? "text-slate-300" : "text-slate-500 dark:text-slate-400"}`}>
                        kr / mån
                      </span>
                    </div>
                  ) : (
                    <div className={`font-display text-3xl font-bold ${popular ? "text-white" : "text-slate-900 dark:text-white"}`}>
                      Skräddarsytt
                    </div>
                  )}
                  {price !== null && (
                    <div className={`mt-1 text-xs ${popular ? "text-slate-400" : "text-slate-400"}`}>
                      {annual ? "Faktureras årligen, exkl. moms" : "Exkl. moms"}
                    </div>
                  )}
                </div>

                <ul className="space-y-3 flex-1">
                  {plan.features.map((f) => (
                    <li key={f} className="flex items-start gap-3">
                      <span className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full ${popular ? "bg-emerald-500" : "bg-emerald-100 dark:bg-emerald-500/15 text-emerald-600 dark:text-emerald-400"}`}>
                        <Check className={`h-3 w-3 ${popular ? "text-white" : ""}`} strokeWidth={3} />
                      </span>
                      <span className={`text-sm ${popular ? "text-slate-200" : "text-slate-700 dark:text-slate-300"}`}>
                        {f}
                      </span>
                    </li>
                  ))}
                </ul>

                <Button
                  data-testid={`${plan.testid}-cta`}
                  onClick={() => handleClick(plan)}
                  className={`mt-8 h-12 rounded-xl font-semibold ${
                    popular
                      ? "bg-emerald-500 hover:bg-emerald-600 text-white shadow-[0_8px_22px_-4px_rgba(16,185,129,0.55)]"
                      : "bg-slate-900 dark:bg-white hover:bg-slate-800 dark:hover:bg-slate-100 text-white dark:text-slate-900"
                  }`}
                >
                  {plan.cta}
                </Button>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
